/* eslint-disable no-loops/no-loops */
/* eslint-disable @typescript-eslint/no-use-before-define */

import {
  Button,
  Card,
  Inline,
  Stack,
  useViewport,
} from '@omnipresentgroup/design-system';
import {
  useDocumentCreate,
  useDocumentUpload,
} from '@omnipresentgroup/domains';
import { Form } from '@omnipresentgroup/flexible-forms';
import { dataURItoFile } from '@omnipresentgroup/helpers';
import {
  DocumentType,
  type ErrorResponse,
  isLegacyErrorResponse,
  type LegacyErrorResponse,
} from '@omnipresentgroup/types';
import type { IChangeEvent } from '@rjsf/core';
import { useState } from 'react';

import { DocumentFormSkeleton } from './components';
import { useDocumentSchema } from './hooks';
import type { ContractDocumentForm, DocumentFormFields } from './types';

const FORM_ID = 'add-document-form';
const SUBMIT = 'Submit';

type DocumentFormProps = {
  readonly?: boolean;
  onSuccess?: (documentType?: DocumentType) => void;
  onError?: (errorMessage?: string) => void;
  allowAddContract?: boolean;
  companyId?: string;
  userId?: string;
};

export const DocumentForm = ({
  userId,
  companyId,
  readonly,
  allowAddContract,
  onSuccess,
  onError,
}: DocumentFormProps) => {
  const viewport = useViewport();

  const [formValues, setFormValues] = useState<
    DocumentFormFields | undefined
  >();

  const {
    schema: flexibleSchema,
    uiSchema: flexibleUiSchema,
    isLoading: isLoadingSchema,
  } = useDocumentSchema({
    formValues,
    allowAddContract,
  });

  const { uploadDocument } = useDocumentUpload();
  const { createDocument } = useDocumentCreate();

  // Form Change
  const handleChange = ({ formData }: IChangeEvent<DocumentFormFields>) => {
    setFormValues(formData);
  };

  // Form Submit
  const handleSubmit = async () => {
    if (formValues?.document) {
      try {
        const uploadDocumentResponse = await uploadDocument({
          fileToUpload: dataURItoFile(formValues.document),
        });

        await createDocument({
          linkToDocument: uploadDocumentResponse.documentUrl,
          type: formValues.type ?? DocumentType.DOCUMENT,
          title: formValues.title,
          description: formValues.description,
          userId: userId,
          tenantId: companyId,
          ...(formValues.type === DocumentType.CONTRACT
            ? {
                metadata: {
                  version: (formValues as ContractDocumentForm).version,
                  status: (formValues as ContractDocumentForm).status,
                },
              }
            : {}),
        });

        onSuccess?.(formValues.type ?? DocumentType.DOCUMENT);
      } catch (error) {
        const errorMessage = isLegacyErrorResponse(error as LegacyErrorResponse)
          ? (error as LegacyErrorResponse).message
          : (error as ErrorResponse).title;
        onError?.(errorMessage);
      }
    }
  };

  return (
    <Inline justify="center">
      <Card maxW={600} p={viewport.isPhone ? '24' : '32'} w="stretch">
        {isLoadingSchema ? (
          <DocumentFormSkeleton />
        ) : (
          <Stack gap="32">
            <Stack gap="16">
              <Form<DocumentFormFields>
                id={FORM_ID}
                schema={flexibleSchema}
                uiSchema={flexibleUiSchema}
                formData={formValues}
                onChange={handleChange}
                readonly={readonly}
                onSubmit={handleSubmit}
              />
            </Stack>
            <Inline justify="end">
              <Button type="submit" form={FORM_ID} disabled={readonly}>
                {SUBMIT}
              </Button>
            </Inline>
          </Stack>
        )}
      </Card>
    </Inline>
  );
};
